import BaseSection from '@/sections/base'
import gsap from '@/core/gsap'
import { prefersReducedMotion } from '@/core/utils/a11y'
import AJAXKlaviyoForm from '@/core/ajaxKlaviyoForm'
import { wireNewsletterForm } from '@/core/wireNewsletterForm'
import NewsletterForm from '@/components/newsletterForm'
import type { ThemeEditorSectionUnloadEvent } from '@/types/shopify'

const selectors = {
  intro: '.page-hero__newsletter-intro',
  formControl: '.form-control',
  input: '.form-control input',
  logo: '.page-hero__newsletter-logo'
}

const classes = {
  // Toggled on <body>; the CSS blurs .header / #view-container / .footer via --page-blur
  pageBlurred: 'is-intro-blurred'
}

// Present in the DOM only while an intro overlay is actually playing
const INTRO_SELECTOR = '[data-intro-court], [data-intro-overlay]'
const REVEAL_EVENT = 'intro.reveal'
// Reveal anyway if the intro never signals, so the newsletter can't get stuck hidden
const REVEAL_FALLBACK_MS = 8000
// With no intro overlay, the hero plays its own reveal shortly after load
const SELF_REVEAL_MS = 250
// Blur applied to the whole page behind the overlay while the intro plays.
// 0 = no blur (no edge artefact at all). Set back to ~6 for a subtle frost.
const PAGE_BLUR = 0

export default class PageHeroSection extends BaseSection {
  static TYPE = 'page-hero'

  newsletterForm: NewsletterForm | null
  ajaxForm: AJAXKlaviyoForm | null

  introEls: HTMLElement[]
  underline: HTMLElement | null
  pageBlurred: boolean
  revealTl: gsap.core.Timeline | null
  revealFallback: number

  constructor(container: HTMLElement) {
    super(container)

    const formEl = this.qs(NewsletterForm.SELECTOR)
    this.newsletterForm = null
    this.ajaxForm = null

    if (formEl) {
      this.newsletterForm = new NewsletterForm(formEl)
      this.ajaxForm = wireNewsletterForm(formEl, this.newsletterForm)
    }

    this.introEls = []
    this.underline = null
    this.pageBlurred = false
    this.revealTl = null
    this.revealFallback = 0

    this.onReveal = this.onReveal.bind(this)
    this.setupIntroReveal()
  }

  setupIntroReveal() {
    const intro = this.container.querySelector<HTMLElement>(selectors.intro)
    const input = this.container.querySelector<HTMLElement>(selectors.input)
    const logo = this.container.querySelector<HTMLElement>(selectors.logo)
    this.underline = this.container.querySelector<HTMLElement>(selectors.formControl)

    this.introEls = [intro, input, logo].filter((el): el is HTMLElement => el !== null)

    // Skip the animation for reduced motion or when the hero has no elements
    if (prefersReducedMotion() || this.introEls.length === 0) {
      return
    }

    gsap.set(intro, { opacity: 0 })
    gsap.set(logo, { opacity: 0 })
    gsap.set(input, { opacity: 0 })
    if (this.underline) gsap.set(this.underline, { '--underline-scale': 0 })

    // Blur the whole page (header, view incl. <video>, footer) while the hero is hidden
    document.body.classList.add(classes.pageBlurred)
    gsap.set(document.body, { '--page-blur': PAGE_BLUR })
    this.pageBlurred = true

    if (document.querySelector(INTRO_SELECTOR)) {
      // An intro overlay is playing → reveal as it lifts (fallback so it can't stick hidden)
      window.addEventListener(REVEAL_EVENT, this.onReveal, { once: true })
      this.revealFallback = window.setTimeout(this.onReveal, REVEAL_FALLBACK_MS)
    }
    else {
      // No intro overlay → the hero plays its own blur-clear + element reveal on load
      this.revealFallback = window.setTimeout(this.onReveal, SELF_REVEAL_MS)
    }
  }

  onReveal() {
    window.clearTimeout(this.revealFallback)
    window.removeEventListener(REVEAL_EVENT, this.onReveal)

    if (this.revealTl) return

    const intro = this.container.querySelector<HTMLElement>(selectors.intro)
    const input = this.container.querySelector<HTMLElement>(selectors.input)
    const logo = this.container.querySelector<HTMLElement>(selectors.logo)

    this.revealTl = gsap.timeline({
      delay: 0.15,
      onComplete: () => {
        gsap.set(this.introEls.concat(this.underline ?? []), { clearProps: 'all' })
        this.clearPageBlur()
      }
    })

    this.revealTl
      // Un-blur the page as the overlay lifts
      .to(document.body, { '--page-blur': 0, duration: 0.9, ease: 'power2.inOut' }, 0)
      // Text, logo and field simply fade in — no slide
      .to(intro, { opacity: 1, duration: 0.6, ease: 'power2.out' }, 0)
      .to(logo, { opacity: 1, duration: 0.8, ease: 'power1.out' }, 0)
      .to(input, { opacity: 1, duration: 0.5, ease: 'power1.out' }, 0.25)
      // …then the underline draws in, a beat later (a touch slower)
      .to(this.underline, { '--underline-scale': 1, duration: 0.9, ease: 'power2.inOut' }, 0.45)
  }

  clearPageBlur() {
    if (!this.pageBlurred) return
    document.body.classList.remove(classes.pageBlurred)
    gsap.set(document.body, { clearProps: '--page-blur' })
    this.pageBlurred = false
  }

  onUnload(e: ThemeEditorSectionUnloadEvent) {
    window.clearTimeout(this.revealFallback)
    window.removeEventListener(REVEAL_EVENT, this.onReveal)
    this.revealTl?.kill()
    this.clearPageBlur()
    this.newsletterForm?.destroy()
    this.ajaxForm?.destroy()

    super.onUnload(e)
  }
}
